import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { DeploymentItem } from '../../components/message-composer/chat-context/selectors';
import {
  fetchPortalInstancesPage,
  formatPortalInstanceName,
  PORTAL_PAGE_SIZE,
  type PortalInstance,
} from '../../portal/portalClient';
import { useInstances } from '../useInstances';
import { instanceQueryKey } from './useInstanceLabel';
import { usePortalAccess } from './usePortalAccess';

const STALE_TIME = 5 * 60 * 1000;

/** Shared, so an omitted `products` is the same reference every render. */
const EMPTY_PRODUCTS: string[] = [];

const toItem = (instance: PortalInstance): DeploymentItem => {
  const id = instance.id as string;
  return { id, label: formatPortalInstanceName(instance) || id };
};

/**
 * The deployment selector's list.
 *
 * The source is the portal only when the feature is on *and* a portal host is configured
 * (`isPortalSource`) — not `isEnabled` alone. Flag-on-with-no-host must fall back to the guardian
 * list, the same as flag-off: otherwise the portal query stays permanently disabled,
 * `query.isPending` never resolves, and the selector reports 'loading' forever (see
 * `useKnownInstanceIds` for the same reasoning applied to id resolution).
 *
 * Flag on: one ranked page of `PORTAL_PAGE_SIZE` from the portal, filtered server-side.
 * Ranking happens before pagination there, so page 1 already holds the best matches and
 * there is nothing to page through. `keepPreviousData` holds the old rows on screen while
 * a new search is in flight, so the menu never blanks between keystrokes. While `isPortalSource`
 * is true but the auth token hasn't arrived yet, `canQuery` is false and the query stays
 * disabled+pending — which correctly reports as 'loading', since we are genuinely waiting
 * on auth rather than on a misconfiguration.
 *
 * Flag off (or on-but-inert): the guardian id list, labelled by id — `search` is ignored,
 * because filtering is not done here on that path; `isServerFiltered: false` tells the
 * caller (`DeploymentSelector`, via `ChatContextualizer`) that it must keep its own local
 * substring filter, since nothing upstream has narrowed the list. The guardian request
 * itself is only made on this path (`useInstances({ enabled: !isPortalSource })`): when the
 * portal is the source, firing the guardian request too would be pure waste, since
 * `useInstances` carries `initialData` and no guard of its own.
 *
 * `search` must already be debounced by the caller.
 *
 * `products` narrows the list to those product families (the portal's `product` filter), for
 * a caller that only makes sense for some of them — a dashboard declaring its `products`.
 * Empty or absent means every product. It only bites on the portal path: the guardian
 * fallback list is bare ids with no product attached, so nothing there can be narrowed,
 * and a caller relying on the constraint must expect the wider list when the flag is off.
 */
export const useInstanceOptions = (
  search: string,
  products: string[] = EMPTY_PRODUCTS,
): {
  items: DeploymentItem[];
  totalCount: number;
  status: 'loading' | 'success' | 'error';
  /**
   * Whether `items` has already been filtered (and ranked) by `search` — true only on the
   * portal path. The caller must not pass its own `search` box's `onSearchChange` straight
   * through to a component that also disables local filtering on that prop's mere presence
   * (`DeploymentSelector` does) unless this is true, or matches on the guardian path would
   * be silently dropped.
   */
  isServerFiltered: boolean;
  /**
   * A search request is in flight. Distinct from `status`: `keepPreviousData` deliberately
   * holds the previous page on screen so the menu never blanks between keystrokes, which
   * also means `status` stays `success` throughout a refetch. Without this the caller has
   * no way to tell the user that the rows they are looking at are about to change.
   */
  isSearching: boolean;
} => {
  const { baseUrl, token, canQuery, isPortalSource } = usePortalAccess();

  const {
    data: { instances: guardianInstances },
    status: guardianStatus,
  } = useInstances({
    enabled: !isPortalSource,
  });

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['portal-instances', baseUrl, 'search', search, 'products', products],
    queryFn: async () => {
      const page = await fetchPortalInstancesPage({ baseUrl, token, search, products, size: PORTAL_PAGE_SIZE });
      /*
       * Every row is a full portal record, and the selected chip resolves its label through
       * the shared by-id cache — without priming, picking a row refetches what we already
       * hold and the chip flashes the raw id. Same as the MCP id translation.
       *
       * In the queryFn rather than an effect: `keepPreviousData` changes `query.data`
       * identity on every keystroke, so an effect would rewrite records it just wrote. Here
       * it is one write per network response, and it lands before the rows paint. `enabled:
       * canQuery` already implies the portal is the source, so no guard is needed.
       */
      for (const instance of page.instances) {
        if (instance.id) queryClient.setQueryData(instanceQueryKey(baseUrl, instance.id), instance);
      }
      return page;
    },
    enabled: canQuery,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const portalItems = useMemo(
    () => (query.data?.instances ?? []).filter((instance) => instance.id).map(toItem),
    [query.data],
  );

  const guardianItems = useMemo(() => guardianInstances.map((id) => ({ id, label: id })), [guardianInstances]);

  return useMemo(() => {
    if (!isPortalSource) {
      return {
        items: guardianItems,
        totalCount: guardianItems.length,
        // `useInstances` carries initialData, so React Query never reports `pending` for it —
        // its status is only ever success or error. No loading state to map on this path.
        status: guardianStatus,
        isServerFiltered: false,
        isSearching: false,
      };
    }

    return {
      items: portalItems,
      // The portal always sends totalCount, and portalClient normalises a missing one to 0 —
      // so a `??` fallback here would be dead code. Floor it instead: the count must never
      // claim fewer instances than the rows we are actually showing, or the selector footer
      // would read "20 of 0".
      totalCount: Math.max(query.data?.totalCount ?? 0, portalItems.length),
      status: query.isError ? ('error' as const) : query.isPending ? ('loading' as const) : ('success' as const),
      isServerFiltered: true,
      isSearching: query.isFetching,
    };
  }, [
    isPortalSource,
    guardianItems,
    guardianStatus,
    portalItems,
    query.data,
    query.isError,
    query.isPending,
    query.isFetching,
  ]);
};
